
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Service } from '@/components/services/types';
import { Product } from '@/components/services/products/types';

export const useServicesData = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isServicesLoading, setIsServicesLoading] = useState(true);
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      setIsServicesLoading(true);
      try {
        const { data, error } = await supabase
          .from('professional_services')
          .select(`
            id, professional_id, service_name, description, price, duration,
            photo_url, photos, address, phone, email, country, category, keywords,
            created_at, updated_at,
            profiles:professional_id (
              full_name, profile_photo, rating, response_rate, response_time, 
              total_reviews, about_me, experience, education, languages, certifications
            )
          `);

        if (error) throw error;
        setServices(data || []);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError(err instanceof Error ? err : new Error('Unknown error fetching services'));
      } finally {
        setIsServicesLoading(false);
      }
    };

    const fetchProducts = async () => {
      setIsProductsLoading(true);
      try {
        const { data, error } = await supabase
          .from('professional_products')
          .select(`
            id, professional_id, title, description, price, photo_url, photos,
            address, phone, condition, created_at, updated_at, currency, quotes,
            profiles:professional_id (
              full_name, profile_photo, rating, response_rate, response_time, 
              total_reviews, practice_address, specialties
            )
          `);

        if (error) throw error;
        
        // Transform data to match Product type
        const transformedProducts: Product[] = (data || []).map(product => {
          // Parse quotes if needed
          let parsedQuotes = [];
          if (product.quotes) {
            try {
              if (typeof product.quotes === 'string') {
                parsedQuotes = JSON.parse(product.quotes);
              } else if (Array.isArray(product.quotes)) {
                parsedQuotes = product.quotes;
              } else if (typeof product.quotes === 'object') {
                parsedQuotes = [product.quotes];
              }
            } catch (e) {
              console.error("Error parsing quotes:", e);
              parsedQuotes = [];
            }
          }
          
          return {
            ...product,
            quotes: parsedQuotes,
            currency: product.currency || "EUR",
            photos: Array.isArray(product.photos) ? product.photos : [],
            address: product.address || "",
            phone: product.phone || "",
            condition: product.condition || "",
            profiles: product.profiles || {
              full_name: "",
              profile_photo: "",
              rating: 0,
              response_rate: 0,
              response_time: 0, 
              total_reviews: 0
            }
          };
        });
        
        setProducts(transformedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err : new Error('Unknown error fetching products'));
      } finally {
        setIsProductsLoading(false);
      }
    };

    fetchServices();
    fetchProducts();
  }, []);

  return {
    services,
    products,
    isServicesLoading,
    isProductsLoading,
    error
  };
};
